/* eslint-disable no-nested-ternary */
import { Button, Layout, Loader } from 'uikit';
import { isNumber } from 'utils';

import { IconBackward } from '@consta/icons/IconBackward';
import type React from 'react';
import type { PropsWithChildren, ReactElement } from 'react';
import { useMemo, useRef, useEffect, useState } from 'react';

import { ProjectPageLayout } from '../Layouts/ProjectPageLayout';

import { useIncludeExcludeWorkOrderObjects } from './hooks/useIncludeExcludeWorkOrderObjects';
import type { IWorkOrderStructurePageLayout } from './types';
import { WorkOrderStructure } from './WorkOrderStructure';

import Style from './WorkOrderStructurePageLayout.module.css';

export const WorkOrderStructurePageLayout = <T,>(
    props: PropsWithChildren<IWorkOrderStructurePageLayout<T>>,
): ReactElement<any, any> | null => {
    const {
        isObjectsFormationStage,
        isFrameContract,
        methodology,
        projectData,
        useGetDataSource,
        menuItemsGetter,
        WorkOrderObjectEditComponent,
        WorkOrderSubObjectEditComponent,
        workOrderOrChangeId,
        canEditObjectCode,
        isReadonly,
        workOrderFullNumber,
        useWorkOrderEditObjectDataSource,
        useRemoveWorkOrderObject,
        useGetIncludedCountData,
        skipEntityNameInTitle,
        isTopFilterReadOnly,
        status,
    } = props;

    const [workOrderStructureStyle, setWorkOrderStructureStyle] = useState<React.CSSProperties>();

    const tableRef = useRef<HTMLDivElement>(null);

    const {
        complete,
        isCompletePerforming,
        isLoading,
        crudMenuConfig,
        items,
        constructionTypes,
        seasons,
        includedCount,
        topFilterConfig,
        onItemCheckChange,
        areCheckboxesDisabled,
        onCheckAll,
        disabledCheckBoxesItemsIds,
        dragOperationDisabledItemsIds,
        checkConfirmations,
        checkedIds,
        selectedSeasonsIds,
        informers,
        setConstructionTypeId,
        constructionTypeId,
        isHeaderCheckboxDisabled,
        checkBoxesNotifications,
        getStructureOperationConfirmationMessage,
    } = useGetDataSource({
        projectData,
        methodology,
        workOrderOrChangeId,
        canEditObjectCode,
        useGetIncludedCountData,
        useIncludeExcludeWorkOrderObjects,
        isReadOnly: isReadonly,
        isTopFilterReadOnly,
        status,
    });

    const workOrderMessageTitle = useMemo(
        () => (skipEntityNameInTitle ? '' : isFrameContract ? 'Наряд-Заказа' : 'Расчета по договору'),
        [isFrameContract, skipEntityNameInTitle],
    );

    const title = useMemo(() => {
        if (isReadonly) {
            return `Структура ${workOrderMessageTitle} ${workOrderFullNumber}`;
        }

        return isObjectsFormationStage
            ? `Формирование структуры ${workOrderMessageTitle} ${workOrderFullNumber}`
            : `Редактирование структуры ${workOrderMessageTitle} ${workOrderFullNumber}`;
    }, [isObjectsFormationStage, isReadonly, workOrderFullNumber, workOrderMessageTitle]);

    const saveButtonTitle = useMemo(() => {
        if (isReadonly) {
            return 'Вернуться';
        }

        return isObjectsFormationStage ? 'Завершить формирование' : 'Завершить редактирование';
    }, [isObjectsFormationStage, isReadonly]);

    const firstRowInformerMessage = useMemo(
        () =>
            isObjectsFormationStage
                ? `Выберите объекты для включения объекта и вида строительства в расчет ${workOrderMessageTitle}`
                : undefined,
        [isObjectsFormationStage, workOrderMessageTitle],
    );

    const breadCrumbsItems = useMemo(() => {
        const lowerMethodology = methodology?.toLowerCase();

        return [
            {
                label: projectData?.name,
                href: `/projects/${lowerMethodology}/${projectData?.uniqueId}`,
            },
            {
                label: 'Договор',
                href: `/projects/${lowerMethodology}/${projectData?.uniqueId}/contract`,
            },
        ];
    }, [methodology, projectData?.name, projectData?.uniqueId]);

    useEffect(() => {
        const onResize = () => {
            if (tableRef.current) {
                setWorkOrderStructureStyle({
                    '--others-height':
                        isNumber(tableRef.current?.getBoundingClientRect?.()?.top) &&
                        `${
                            (tableRef.current?.getBoundingClientRect?.()?.top as number) +
                            (firstRowInformerMessage ? 145 : 145)
                        }px`,
                } as React.CSSProperties);
            }
        };

        window.addEventListener('resize', onResize);

        onResize();

        return () => {
            window.removeEventListener('resize', onResize);
        };
    }, [firstRowInformerMessage]);

    return (
        <ProjectPageLayout title={title} breadCrumbsItems={breadCrumbsItems} style={{ height: 'calc(100% - 60px)' }}>
            <Layout className="wrapperToolbarLine">
                <Button
                    style={{ marginRight: '10px' }}
                    label={saveButtonTitle}
                    size="s"
                    loading={isCompletePerforming}
                    onClick={complete}
                    iconLeft={isReadonly ? IconBackward : undefined}
                />
            </Layout>
            <div id="workOrderStructure" style={workOrderStructureStyle}>
                {isLoading ? (
                    <Loader />
                ) : (
                    <div className={Style.workOrderStructureContainer} ref={tableRef}>
                        <WorkOrderStructure
                            items={items}
                            crudMenuConfig={crudMenuConfig}
                            menuItemsGetter={menuItemsGetter}
                            informerMessage={firstRowInformerMessage}
                            constructionTypes={constructionTypes}
                            seasons={seasons}
                            WorkOrderObjectEditComponent={WorkOrderObjectEditComponent}
                            WorkOrderSubObjectEditComponent={WorkOrderSubObjectEditComponent}
                            includedCount={includedCount}
                            topFilterConfig={topFilterConfig}
                            onItemCheckChange={onItemCheckChange}
                            areCheckboxesDisabled={areCheckboxesDisabled}
                            onCheckAll={onCheckAll}
                            disabledCheckBoxesItemsIds={disabledCheckBoxesItemsIds}
                            dragOperationDisabledItemsIds={dragOperationDisabledItemsIds}
                            checkConfirmations={checkConfirmations}
                            checkedIds={checkedIds}
                            selectedSeasonsIds={selectedSeasonsIds}
                            informers={informers}
                            onLeftFilterRowSelected={setConstructionTypeId}
                            isReadOnly={isReadonly}
                            useWorkOrderEditObjectDataSource={useWorkOrderEditObjectDataSource}
                            constructionTypeId={constructionTypeId}
                            contractId={projectData?.contractId}
                            workOrderOrChangeId={workOrderOrChangeId}
                            canEditObjectCode={canEditObjectCode}
                            useRemoveWorkOrderObject={useRemoveWorkOrderObject}
                            isHeaderCheckboxDisabled={isHeaderCheckboxDisabled}
                            checkBoxesNotifications={checkBoxesNotifications}
                            getStructureOperationConfirmationMessage={getStructureOperationConfirmationMessage}
                        />
                    </div>
                )}
            </div>
        </ProjectPageLayout>
    );
};

WorkOrderStructurePageLayout.displayName = 'WorkOrderStructurePageLayout';
